import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { useLabelStore } from "../../../store/labelStore";
import { LabelTemplate } from "../../../types/label";
import { PrintableLabel } from "../../../components/designer/PrintableLabel";
import { CanvasElement } from "../../../components/designer/types";
import { Printer } from "lucide-react";
import { formatQuantity } from "../../../utils/format";

interface RollPrintDialogProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRolls: any[];
}

export const RollPrintDialog: React.FC<RollPrintDialogProps> = ({
    isOpen, onClose, selectedRolls
}) => {
    const { fetchDataDynamic } = useLabelStore();
    const [templates, setTemplates] = useState<LabelTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<LabelTemplate | null>(null);
    const [copiesPerRoll, setCopiesPerRoll] = useState<number>(1);
    
    // Derived values for the template
    const [elements, setElements] = useState<CanvasElement[]>([]);
    const [widthMm, setWidthMm] = useState(100);
    const [heightMm, setHeightMm] = useState(100);
    
    const [isPrinting, setIsPrinting] = useState(false);
    
    useEffect(() => {
        if (isOpen) {
            loadTemplates();
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedTemplate) {
            const variant = selectedTemplate.variants && selectedTemplate.variants.length > 0 
                ? selectedTemplate.variants[0] 
                : null;
            if (variant) {
                setWidthMm(variant.widthMm || 100);
                setHeightMm(variant.heightMm || 100);
                try {
                    const parsedElements = JSON.parse(variant.content);
                    setElements(Array.isArray(parsedElements) ? parsedElements : []);
                } catch (e) {
                    console.error("Template parse error", e);
                    setElements([]);
                }
            }
        }
    }, [selectedTemplate]);

    const loadTemplates = async () => {
        try {
            // Sadece PRODUCT ve OTHER kategorilerini çek (veya hepsini)
            const data = await fetchDataDynamic({ pageIndex: 1, pageSize: 100 });
            const items = data.items || [];
            // Filtreleme yapılabilir ama şimdilik hepsini gösterelim
            setTemplates(items);
        } catch (error) {
            console.error("Şablonlar yüklenemedi:", error);
        }
    };

    const handlePrint = () => {
        setIsPrinting(true);
        setTimeout(() => {
            window.print();
            setIsPrinting(false);
            onClose();
        }, 500); // UI render for print
    };

    // Baskı için kaç adet sayfa (etiket) çıkacağı hesaplanıyor
    const printItems = [];
    for (const roll of selectedRolls) {
        for (let i = 0; i < copiesPerRoll; i++) {
            printItems.push(roll);
        }
    }

    return (
        <>
            <Dialog
                visible={isOpen && !isPrinting} // Baskı anında ana modalı gizle ki sadece sayfa yazdırılsın
                onHide={onClose}
                header="Top (Rulo) Etiketi Yazdır"
                className="w-full sm:w-[500px]"
                modal
            >
                <div className="flex flex-col gap-4 p-2">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Etiket Şablonu</label>
                        <Dropdown 
                            value={selectedTemplate} 
                            options={templates} 
                            onChange={(e) => setSelectedTemplate(e.value)} 
                            optionLabel="name" 
                            placeholder="Şablon Seçiniz" 
                            className="w-full" 
                        />
                    </div>
                    
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Her Top İçin Kopya Sayısı</label>
                        <InputNumber 
                            value={copiesPerRoll} 
                            onValueChange={(e) => setCopiesPerRoll(e.value || 1)} 
                            min={1} 
                            max={100} 
                            className="w-full" 
                        />
                    </div>
                    
                    <div className="p-4 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-200">
                        Toplam <strong>{selectedRolls.length}</strong> top için, her birinden <strong>{copiesPerRoll}</strong> adet olmak üzere toplam <strong>{printItems.length}</strong> etiket yazdırılacak.
                    </div>

                    <div className="flex justify-end mt-4">
                        <Button 
                            label="Yazdır" 
                            icon={<Printer className="w-4 h-4 mr-2" />} 
                            onClick={handlePrint} 
                            disabled={!selectedTemplate || selectedRolls.length === 0}
                            className="bg-[var(--primary)] text-white px-6 py-2 rounded-lg"
                        />
                    </div>
                </div>
            </Dialog>

            {/* Print Container: Sadece baskı esnasında (veya print css ile) görünür olacak */}
            {isPrinting && (
                <div className="print-container">
                    <style>{`
                        @media print {
                            @page {
                                size: ${widthMm}mm ${heightMm}mm;
                                margin: 0;
                            }
                        }
                    `}</style>
                    {printItems.map((roll, index) => (
                        <PrintableLabel 
                            key={`${roll.id}-${index}`}
                            elements={elements}
                            widthMm={widthMm}
                            heightMm={heightMm}
                            data={{
                                barcode: roll.barcode,
                                rollCode: roll.rollCode,
                                initialQuantity: formatQuantity(roll.initialQuantity),
                                currentQuantity: formatQuantity(roll.currentQuantity),
                                rollMeters: formatQuantity(roll.currentQuantity),
                                stock: roll.stock?.name,
                                variant: roll.variant?.name,
                                productName: roll.stock?.name, // alias
                                variantName: roll.variant?.name, // alias
                                warehouse: roll.warehouse?.name,
                                sku: roll.stock?.sku || roll.variant?.sku,
                                price: roll.stock?.prices?.[0]?.price || roll.stock?.price || 0,
                                ...roll
                            }}
                        />
                    ))}
                </div>
            )}
        </>
    );
};
